import {
    Component,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Output,
    EventEmitter,
    Input,
    OnChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import moment from 'moment';
import { CustomDateRange } from 'locations/station-dialog/status/timeline-controls/custom-date-range/custom-date-range.component';
import { DownloadLocationPowerGraphAsExcelQueryParameters } from '@kempoweroyj/ngx-reporting-api-client';
import { TimeDisplayService } from 'shared/time-display-service/time-display.service';

export interface DownloadLocationPowerGraphAsExcelRequest {
    locationUid?: string;
    stationId?: string;
    queryParameters: DownloadLocationPowerGraphAsExcelQueryParameters;
}
export type TimelineZoomLevel = 'DAY' | 'WEEK' | 'HOUR' | 'CUSTOM';

@Component({
    selector: 'ce-timeline-controls',
    templateUrl: './timeline-controls.component.html',
    styleUrls: ['./timeline-controls.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class TimelineControlsComponent implements OnChanges {
    @Input() startTime: string;
    @Output() startTimeChange = new EventEmitter<string>();
    @Input() endTime: string;
    @Output() endTimeChange = new EventEmitter<string>();
    @Input() seekTime: string;
    @Output() seekTimeChange = new EventEmitter<string>();
    @Input() zoomLevel: TimelineZoomLevel;
    @Output() zoomLevelChange = new EventEmitter<TimelineZoomLevel>();
    @Input() stationId: string;
    @Input() locationUid: string;

    @Input() hideDownloadExcel: boolean;
    @Output() downloadExcelChange = new EventEmitter<DownloadLocationPowerGraphAsExcelRequest>();

    canDownloadExcel = false;
    canSeekForward = true;
    duration: number;
    durationUnit: moment.unitOfTime.Base = 'minute';
    canZoomIn = true;
    canZoomOut = true;
    HOUR_IN_MINUTE = 1 * 60;
    DAY_IN_MINUTE = this.HOUR_IN_MINUTE * 24;
    WEEK_IN_MINUTE = this.DAY_IN_MINUTE * 7;
    currentTime$ = this.timeDisplayService.timeObservable$;

    constructor(
        private cdRef: ChangeDetectorRef,
        private router: Router,
        private timeDisplayService: TimeDisplayService
    ) {}

    ngOnChanges() {
        this.checkCanDownloadExcel();

        this.checkCanZoom();

        this.checkCanSeekForward();

        this.cdRef.markForCheck();
    }

    zoomIn() {
        if (this.zoomLevel === 'WEEK') {
            this.zoomLevelChange.emit('DAY');
        } else if (this.zoomLevel === 'DAY') {
            this.zoomLevelChange.emit('HOUR');
        } else if (this.zoomLevel === 'CUSTOM') {
            const durationMinute = moment.duration(this.duration, this.durationUnit).asMinutes();

            if (durationMinute >= this.WEEK_IN_MINUTE) {
                this.zoomLevelChange.emit('WEEK');
            } else if (durationMinute >= this.DAY_IN_MINUTE && durationMinute < this.WEEK_IN_MINUTE) {
                this.zoomLevelChange.emit('DAY');
            } else {
                this.zoomLevelChange.emit('HOUR');
            }
        }
    }

    zoomOut() {
        if (this.zoomLevel === 'DAY') {
            this.zoomLevelChange.emit('WEEK');
        } else if (this.zoomLevel === 'HOUR') {
            this.zoomLevelChange.emit('DAY');
        } else if (this.zoomLevel === 'CUSTOM') {
            const durationMinute = moment.duration(this.duration, this.durationUnit).asMinutes();

            if (durationMinute <= this.HOUR_IN_MINUTE) {
                this.zoomLevelChange.emit('HOUR');
            } else if (durationMinute <= this.DAY_IN_MINUTE && durationMinute > this.HOUR_IN_MINUTE) {
                this.zoomLevelChange.emit('DAY');
            } else {
                this.zoomLevelChange.emit('WEEK');
            }
        }
    }

    zoomReset() {
        this.zoomLevelChange.emit('DAY');
        this.seekTimeChange.emit(null);
        this.startTimeChange.emit(null);
        this.endTimeChange.emit(null);
    }

    checkCanZoom() {
        const { duration, durationUnit } = calculateDurationAndZoomLevel(this.startTime, this.endTime);
        // Update component's duration and durationUnit
        this.duration = duration;
        this.durationUnit = durationUnit;

        // Check canZoomIn and canZoomOut
        const durationMinute = moment.duration(duration, durationUnit).asMinutes();
        // zoom in logic
        if (this.zoomLevel === 'HOUR' || (this.zoomLevel === 'CUSTOM' && durationMinute <= this.HOUR_IN_MINUTE)) {
            this.canZoomIn = false;
        } else {
            this.canZoomIn = true;
        }

        // zoom out logic
        if (this.zoomLevel === 'WEEK' || (this.zoomLevel === 'CUSTOM' && durationMinute >= this.WEEK_IN_MINUTE)) {
            this.canZoomOut = false;
        } else {
            this.canZoomOut = true;
        }
    }

    checkCanSeekForward() {
        const now = new Date().toISOString();

        if (moment(this.endTime).isAfter(now)) {
            this.canSeekForward = false;
        } else {
            this.canSeekForward = true;
        }
    }

    seek(signedUnits: number) {
        // Only update seekTime here and update startTime, endTime in status.component if zoomLevel !== 'CUSTOM'
        if (this.zoomLevel !== 'CUSTOM') {
            const wantedSeekTime = moment(this.seekTime)
                .add(signedUnits, this.zoomLevel.toLowerCase() as any)
                .toISOString();
            this.seekTimeChange.emit(wantedSeekTime);
        } else {
            let wantedSignedUnits: number;
            if (!this.duration) {
                wantedSignedUnits = moment(this.endTime).diff(moment(this.startTime), this.durationUnit) * signedUnits;
            } else {
                wantedSignedUnits = this.duration * signedUnits;
            }

            const wantedEndTime = moment(this.endTime).add(wantedSignedUnits, this.durationUnit).toISOString();
            const wantedStartTime = moment(this.startTime).add(wantedSignedUnits, this.durationUnit).toISOString();
            const wantedSeekTime = moment(this.seekTime).add(wantedSignedUnits, this.durationUnit).toISOString();

            this.seekTimeChange.emit(wantedSeekTime);
            this.endTimeChange.emit(wantedEndTime);
            this.startTimeChange.emit(wantedStartTime);
        }
    }

    customDateRangeChange(data: { dates: CustomDateRange }) {
        const wantedStartTime = data.dates.startDate.toISOString();
        const wantedEndTime = data.dates.endDate.toISOString();

        // Change zoom level and endTime based on duration
        const { zoomLevel, duration, durationUnit, isValidDuration, validDuration, validEndTime } =
            calculateDurationAndZoomLevel(wantedStartTime, wantedEndTime);

        if (isValidDuration) {
            this.durationUnit = durationUnit;

            this.duration = duration;
            this.zoomLevelChange.emit(zoomLevel);
            this.seekTimeChange.emit(wantedStartTime);
            this.startTimeChange.emit(wantedStartTime);
            this.endTimeChange.emit(wantedEndTime);
        } else {
            this.durationUnit = durationUnit;

            this.duration = validDuration;
            this.zoomLevelChange.emit(zoomLevel);
            this.seekTimeChange.emit(wantedStartTime);
            this.startTimeChange.emit(wantedStartTime);
            this.endTimeChange.emit(validEndTime); // keep startTime, change endTime to valid one
        }
    }

    downloadExcel() {
        // check
        this.checkCanDownloadExcel();

        if (this.canDownloadExcel) {
            // form request
            const request: DownloadLocationPowerGraphAsExcelRequest = {
                ...(this.stationId ? { stationId: this.stationId } : { locationUid: this.locationUid }),
                queryParameters: {
                    startTime: this.startTime,
                    endTime: this.endTime,
                },
            };

            // emit
            this.downloadExcelChange.emit(request);
        }
    }

    checkCanDownloadExcel() {
        if (!this.locationUid && !this.stationId) {
            // disable button if none of these exists
            this.canDownloadExcel = false;
        } else {
            this.canDownloadExcel = true;
        }

        this.cdRef.markForCheck();
    }

    openDialogClick() {
        this.router.navigate(['', { outlets: { d: ['timeline', this.stationId] } }]);
    }
}

export const MAX_TIMELINE_LIMIT_MINUTES = 60 * 24 * 7 * 2; // 2 weeks
export const MIN_TIMELINE_LIMIT_MINUTES = 5;

export function calculateDurationAndZoomLevel(
    startTime: string,
    endTime: string,
    durationUnit: moment.unitOfTime.Base = 'minute'
) {
    let zoomLevel: TimelineZoomLevel = 'CUSTOM';
    const duration = moment(endTime).diff(moment(startTime), durationUnit, true); // precise number
    let durationMinute: number = duration;
    let isValidDuration = true;
    let validEndTime: string = endTime;
    let validDuration: number = duration;

    // Check for a valid duration and return validEndTime
    if (durationUnit !== 'minute' && durationUnit !== 'minutes') {
        durationMinute = moment(endTime).diff(moment(startTime), 'minute', true);
    }
    if (durationMinute > MAX_TIMELINE_LIMIT_MINUTES || durationMinute < MIN_TIMELINE_LIMIT_MINUTES) {
        isValidDuration = false;

        if (durationMinute > MAX_TIMELINE_LIMIT_MINUTES) {
            validEndTime = moment(startTime).add(MAX_TIMELINE_LIMIT_MINUTES, 'minute').toISOString();
            validDuration = MAX_TIMELINE_LIMIT_MINUTES;
        } else {
            validEndTime = moment(startTime).add(MIN_TIMELINE_LIMIT_MINUTES, 'minute').toISOString();
            validDuration = MIN_TIMELINE_LIMIT_MINUTES;
        }
    }

    // Check if start time, end time falls to default zoomLevel, granularity to minute
    // moment.startOf mutates the original moment, hence the repetition
    const startMoment = moment(startTime);
    const endMoment = moment(endTime);
    if (
        startMoment.isSame(moment(startTime).startOf('hour'), 'minute') &&
        endMoment.isSame(moment(startTime).endOf('hour'), 'minute')
    ) {
        zoomLevel = 'HOUR';
    } else if (
        startMoment.isSame(moment(startTime).startOf('day'), 'minute') &&
        endMoment.isSame(moment(startTime).endOf('day'), 'minute')
    ) {
        zoomLevel = 'DAY';
    } else if (
        startMoment.isSame(moment(startTime).startOf('week'), 'minute') &&
        endMoment.isSame(moment(startTime).endOf('week'), 'minute')
    ) {
        zoomLevel = 'WEEK';
    } else {
        zoomLevel = 'CUSTOM';
    }

    return { duration, zoomLevel, durationUnit, isValidDuration, validDuration, validEndTime };
}
